import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { validateRoomInput, type Room, type RoomInput } from "../../domain/room";
import type { RoomType } from "../../domain/roomType";
import type { MeasurementSource } from "../../domain/measurement";
import { ValidationError } from "./projectRepository";

interface RoomRow {
  id: string;
  floor_id: string;
  name: string;
  room_type_id: string;
  floor_area_x100: number | null;
  floor_area_source: string | null;
  perimeter_x100: number | null;
  perimeter_source: string | null;
  ceiling_height_inches: number | null;
  length_x100?: number | null;
  width_x100?: number | null;
  notes: string;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface RoomTypeRow {
  id: string;
  display_name: string;
  sort_order: number;
  is_system: number;
  archived_at: string | null;
}

function mapRoom(row: RoomRow): Room {
  return {
    id: row.id,
    floorId: row.floor_id,
    name: row.name,
    roomTypeId: row.room_type_id,
    floorAreaX100: row.floor_area_x100 === null ? null : Number(row.floor_area_x100),
    floorAreaSource: row.floor_area_source as MeasurementSource | null,
    perimeterX100: row.perimeter_x100 === null ? null : Number(row.perimeter_x100),
    perimeterSource: row.perimeter_source as MeasurementSource | null,
    ceilingHeightInches:
      row.ceiling_height_inches === null ? null : Number(row.ceiling_height_inches),
    lengthX100: row.length_x100 === null || row.length_x100 === undefined ? null : Number(row.length_x100),
    widthX100: row.width_x100 === null || row.width_x100 === undefined ? null : Number(row.width_x100),
    notes: row.notes,
    sortOrder: Number(row.sort_order),
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export class RoomRepository {
  constructor(private readonly db: DbExecutor) {}

  // ----- Room types -----

  async listRoomTypes(): Promise<RoomType[]> {
    const rows = await this.db.select<RoomTypeRow>(
      "SELECT id, display_name, sort_order, is_system, archived_at FROM room_types ORDER BY sort_order, display_name",
    );
    return rows.map((r) => ({
      id: r.id,
      displayName: r.display_name,
      sortOrder: Number(r.sort_order),
      isSystem: Number(r.is_system) === 1,
      archivedAt: r.archived_at,
    }));
  }

  async createRoomType(displayName: string): Promise<RoomType> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Room type name is required."]);
    }
    const existing = await this.listRoomTypes();
    const maxSort = existing.reduce((m, t) => Math.max(m, t.sortOrder), 0);
    const roomType: RoomType = {
      id: generateId(),
      displayName: name,
      sortOrder: maxSort + 10,
      isSystem: false,
      archivedAt: null,
    };
    await this.db.execute(
      "INSERT INTO room_types (id, display_name, sort_order, is_system) VALUES (?, ?, ?, 0)",
      [roomType.id, roomType.displayName, roomType.sortOrder],
    );
    return roomType;
  }

  async renameRoomType(id: string, displayName: string): Promise<void> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Room type name is required."]);
    }
    await this.db.execute("UPDATE room_types SET display_name = ? WHERE id = ?", [name, id]);
  }

  async archiveRoomType(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE room_types SET archived_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), id],
    );
  }

  async unarchiveRoomType(id: string): Promise<void> {
    await this.db.execute("UPDATE room_types SET archived_at = NULL WHERE id = ?", [id]);
  }

  // ----- Rooms -----

  /** All rooms across a project's floors (archived included; callers filter). */
  async listByProject(projectId: string): Promise<Room[]> {
    const rows = await this.db.select<RoomRow>(
      `SELECT r.* FROM rooms r
         JOIN floors f ON f.id = r.floor_id
        WHERE f.project_id = ?
        ORDER BY r.sort_order, r.created_at`,
      [projectId],
    );
    return rows.map(mapRoom);
  }

  private async floorProject(floorId: string): Promise<string> {
    const rows = await this.db.select<{ project_id: string }>(
      "SELECT project_id FROM floors WHERE id = ?",
      [floorId],
    );
    if (rows.length === 0) {
      throw new ValidationError(["The floor for this room no longer exists."]);
    }
    return rows[0].project_id;
  }

  async create(floorId: string, input: RoomInput): Promise<Room> {
    const problems = validateRoomInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.floorProject(floorId); // floor must exist
    const siblings = await this.db.select<{ m: number | null }>(
      "SELECT MAX(sort_order) AS m FROM rooms WHERE floor_id = ?",
      [floorId],
    );
    const maxSort = siblings[0]?.m === null ? 0 : Number(siblings[0].m);
    const now = new Date().toISOString();
    const room: Room = {
      id: generateId(),
      floorId,
      name: input.name.trim(),
      roomTypeId: input.roomTypeId,
      floorAreaX100: input.floorAreaX100,
      floorAreaSource: input.floorAreaX100 === null ? null : "manual",
      perimeterX100: input.perimeterX100,
      perimeterSource: input.perimeterX100 === null ? null : "manual",
      ceilingHeightInches: input.ceilingHeightInches,
      lengthX100: input.lengthX100 ?? null,
      widthX100: input.widthX100 ?? null,
      notes: input.notes,
      sortOrder: maxSort + 10,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO rooms
         (id, floor_id, name, room_type_id, floor_area_x100, floor_area_source,
          perimeter_x100, perimeter_source, ceiling_height_inches, length_x100, width_x100, notes,
          sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        room.id,
        room.floorId,
        room.name,
        room.roomTypeId,
        room.floorAreaX100,
        room.floorAreaSource,
        room.perimeterX100,
        room.perimeterSource,
        room.ceilingHeightInches,
        room.lengthX100,
        room.widthX100,
        room.notes,
        room.sortOrder,
        room.createdAt,
        room.updatedAt,
      ],
    );
    return room;
  }

  async update(id: string, input: RoomInput): Promise<void> {
    const problems = validateRoomInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.db.execute(
      `UPDATE rooms SET
         name = ?, room_type_id = ?,
         floor_area_x100 = ?,
         floor_area_source = CASE WHEN ? IS NULL THEN NULL WHEN floor_area_source IN ('override','geometry') THEN floor_area_source ELSE 'manual' END,
         perimeter_x100 = ?,
         perimeter_source = CASE WHEN ? IS NULL THEN NULL WHEN perimeter_source IN ('override','geometry') THEN perimeter_source ELSE 'manual' END,
         ceiling_height_inches = ?,
         length_x100 = CASE WHEN ? = 1 THEN ? ELSE length_x100 END,
         width_x100 = CASE WHEN ? = 1 THEN ? ELSE width_x100 END,
         notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.name.trim(),
        input.roomTypeId,
        input.floorAreaX100,
        input.floorAreaX100,
        input.perimeterX100,
        input.perimeterX100,
        input.ceilingHeightInches,
        input.lengthX100 === undefined ? 0 : 1,
        input.lengthX100 ?? null,
        input.widthX100 === undefined ? 0 : 1,
        input.widthX100 ?? null,
        input.notes,
        new Date().toISOString(),
        id,
      ],
    );
  }

  /**
   * Phase 7 reconciliation (approved decision 5): record the user's choice
   * for a room measurement when geometry exists — 'geometry' (use the
   * calculated value) or 'override' (keep the manual value). Neither value
   * is destroyed; only the provenance marker changes.
   */
  async setMeasurementSource(id: string, measurement: "floorArea" | "perimeter", source: "geometry" | "override" | "manual"): Promise<void> {
    const column = measurement === "floorArea" ? "floor_area_source" : "perimeter_source";
    await this.db.execute(`UPDATE rooms SET ${column} = ?, updated_at = ? WHERE id = ?`, [source, new Date().toISOString(), id]);
  }

  async getById(id: string): Promise<Room | null> {
    const rows = await this.db.select<RoomRow>(`SELECT r.* FROM rooms r WHERE r.id = ?`, [id]);
    return rows.length === 0 ? null : mapRoom(rows[0]);
  }

  async archive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE rooms SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [now, now, id],
    );
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute("UPDATE rooms SET archived_at = NULL, updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      id,
    ]);
  }
}
